import { 
  collection, 
  getDocs, 
  query, 
  where, 
  orderBy, 
  limit,
  startAfter,
  getDoc,
  doc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
  setDoc,
  writeBatch
} from 'firebase/firestore';
import { db, auth } from '../config/firebase';

export class AdminService {
  // Check if user is admin
  static async isAdmin() {
    if (!auth.currentUser) return false;
    const userDoc = await getDoc(doc(db, 'users', auth.currentUser.uid));
    return userDoc.exists() && userDoc.data().role === 'admin';
  }

  // Get dashboard stats
  static async getDashboardStats() {
    try {
      // Get total users
      const usersSnapshot = await getDocs(collection(db, 'users'));
      const totalUsers = usersSnapshot.size;

      // Get total products
      const productsSnapshot = await getDocs(collection(db, 'products'));
      const totalProducts = productsSnapshot.size;

      // Get orders with product details
      const ordersSnapshot = await getDocs(collection(db, 'orders'));
      const orders = await Promise.all(
        ordersSnapshot.docs.map(async (orderDoc) => {
          const orderData = orderDoc.data();
          
          // Get product details for each order item
          const items = await Promise.all(
            orderData.items.map(async (item) => {
              const productRef = doc(db, 'products', item.productId);
              const productSnap = await getDoc(productRef);
              return {
                ...item,
                product: productSnap.exists() ? productSnap.data() : null
              };
            })
          );

          return {
            id: orderDoc.id,
            ...orderData,
            items
          };
        })
      );

      // Calculate total revenue
      const totalRevenue = orders.reduce((sum, order) => sum + (order.total || 0), 0);

      // Calculate sales by category
      const salesByCategory = {};
      orders.forEach(order => {
        order.items?.forEach(item => {
          if (item.product?.category) {
            salesByCategory[item.product.category] = 
              (salesByCategory[item.product.category] || 0) + (item.quantity || 0);
          }
        });
      });

      // Get recent orders
      const recentOrdersQuery = query(
        collection(db, 'orders'),
        orderBy('createdAt', 'desc'),
        limit(5)
      );
      const recentOrders = (await getDocs(recentOrdersQuery)).docs.map(docSnap => ({
        id: docSnap.id,
        ...docSnap.data()
      }));

      // Get popular products
      const popularProductsQuery = query(
        collection(db, 'products'),
        orderBy('views', 'desc'),
        limit(5)
      );
      const popularProducts = (await getDocs(popularProductsQuery)).docs.map(docSnap => ({
        id: docSnap.id,
        ...docSnap.data()
      }));

      // Get monthly revenue data
      const monthlyRevenue = {};
      orders.forEach(order => {
        if (order.createdAt) {
          const date = order.createdAt.toDate();
          const monthYear = `${date.getMonth() + 1}/${date.getFullYear()}`;
          monthlyRevenue[monthYear] = (monthlyRevenue[monthYear] || 0) + (order.total || 0);
        }
      });

      // Log the data for debugging
      console.log('Sales by Category:', salesByCategory);

      return {
        totalUsers,
        totalProducts,
        totalOrders: ordersSnapshot.size,
        totalRevenue,
        recentOrders,
        popularProducts,
        salesByCategory,
        monthlyRevenue
      };
    } catch (error) {
      console.error('Error getting dashboard stats:', error);
      throw new Error('Failed to get dashboard stats');
    }
  }

  // Initialize default settings
  static async initializeDefaultSettings() {
    try {
      const settingsRef = doc(db, 'settings', 'app');
      const settingsDoc = await getDoc(settingsRef);

      if (!settingsDoc.exists()) {
        const defaultSettings = {
          siteName: 'Auto Parts Marketplace',
          maintenanceMode: false,
          allowNewRegistrations: true,
          maxProductImages: 6,
          minOrderAmount: 0,
          maxOrderItems: 10,
          shippingFee: 0,
          taxRate: 0,
          supportEmail: 'support@autoparts.com',
          supportPhone: '+1234567890',
          termsUrl: '/terms',
          privacyUrl: '/privacy',
          aboutUrl: '/about',
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        };

        await setDoc(settingsRef, defaultSettings);
        console.log('Default settings initialized successfully');
        return defaultSettings;
      }

      return settingsDoc.data();
    } catch (error) {
      console.error('Error initializing default settings:', error);
      throw new Error('Failed to initialize default settings');
    }
  }

  // Get app settings with initialization
  static async getAppSettings() {
    try {
      const settingsRef = doc(db, 'settings', 'app');
      const settingsDoc = await getDoc(settingsRef);

      if (!settingsDoc.exists()) {
        // Initialize with default settings if they don't exist
        return await this.initializeDefaultSettings();
      }

      return settingsDoc.data();
    } catch (error) {
      console.error('Error getting app settings:', error);
      throw new Error('Failed to get app settings');
    }
  }

  // Update app settings
  static async updateAppSettings(settings) {
    try {
      const settingsRef = doc(db, 'settings', 'app');
      
      // First check if document exists
      const settingsDoc = await getDoc(settingsRef);
      
      if (!settingsDoc.exists()) {
        // Create new settings document if it doesn't exist
        await setDoc(settingsRef, {
          ...settings,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });
      } else {
        // Update existing settings
        await updateDoc(settingsRef, {
          ...settings,
          updatedAt: serverTimestamp()
        });
      }
    } catch (error) {
      console.error('Error updating app settings:', error);
      throw new Error('Failed to update app settings');
    }
  }

  // Get all users with pagination
  static async getUsers(lastDoc = null, pageSize = 10) {
    try {
      let q = query(
        collection(db, 'users'),
        orderBy('createdAt', 'desc')
      );

      if (lastDoc) {
        q = query(q, startAfter(lastDoc));
      }

      q = query(q, limit(pageSize));

      const snapshot = await getDocs(q);
      return {
        users: snapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            ...data,
            // Ensure createdAt is a Firestore timestamp
            createdAt: data.createdAt || null
          };
        }),
        lastVisible: snapshot.docs[snapshot.docs.length - 1]
      };
    } catch (error) {
      console.error('Error getting users:', error);
      throw new Error('Failed to get users');
    }
  }

  // Update user role
  static async updateUserRole(userId, role) {
    try {
      await updateDoc(doc(db, 'users', userId), {
        role,
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Error updating user role:', error);
      throw new Error('Failed to update user role');
    }
  }

  // Delete user
  static async deleteUser(userId) {
    try {
      await deleteDoc(doc(db, 'users', userId));
    } catch (error) {
      console.error('Error deleting user:', error);
      throw new Error('Failed to delete user');
    }
  }

  // Get pending products
  static async getPendingProducts() {
    try {
      const q = query(
        collection(db, 'products'),
        where('status', '==', 'pending'),
        orderBy('createdAt', 'desc')
      );

      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error getting pending products:', error);
      throw new Error('Failed to get pending products');
    }
  }

  // Approve product
  static async approveProduct(productId) {
    try {
      if (!auth.currentUser) {
        throw new Error('You must be logged in to modify products');
      }

      const productRef = doc(db, 'products', productId);
      const productDoc = await getDoc(productRef);

      if (!productDoc.exists()) {
        throw new Error('Product not found');
      }

      // Keep the existing hidden status if it exists, otherwise set it to false
      const currentHidden = productDoc.data().hidden ?? false;

      await updateDoc(productRef, {
        status: 'approved',
        hidden: currentHidden, // Preserve hidden status
        approvedAt: serverTimestamp(),
        approvedBy: auth.currentUser.uid
      });
    } catch (error) {
      console.error('Error approving product:', error);
      throw new Error('Failed to approve product');
    }
  }

  // Reject product
  static async rejectProduct(productId, reason) {
    try {
      const productRef = doc(db, 'products', productId);
      await updateDoc(productRef, {
        status: 'rejected',
        rejectedAt: serverTimestamp(),
        rejectedBy: auth.currentUser.uid,
        rejectionReason: reason
      });
    } catch (error) {
      console.error('Error rejecting product:', error);
      throw new Error('Failed to reject product');
    }
  }

  static async updateExistingProductsStatus() {
    try {
      // Get all products
      const snapshot = await getDocs(collection(db, 'products'));
      const batch = writeBatch(db);
      let updateCount = 0;

      snapshot.docs.forEach((doc) => {
        const data = doc.data();
        // Check if status is missing, null, or undefined
        if (!data.status) {
          // Set as approved by default
          batch.update(doc.ref, {
            status: 'approved',
            approvedAt: serverTimestamp(),
            approvedBy: 'system_migration'
          });
          updateCount++;
        }
      });

      if (updateCount > 0) {
        await batch.commit();
      }
      
      console.log(`Updated ${updateCount} products with status`);
      return updateCount;
    } catch (error) {
      console.error('Error updating products status:', error);
      throw new Error('Failed to update products status');
    }
  }
} 